import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Book, ChevronRight, Search, Menu, X } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';

// Documentation content embedded
const DOCUMENTATION_CONTENT = `
# GestMob - Documentation Utilisateur

## Guide complet de l'application de Gestion du Mobilier de Bureau

---

## Table des matières

1. [Présentation](#1-présentation)
2. [Accès et Authentification](#2-accès-et-authentification)
3. [Tableau de Bord](#3-tableau-de-bord)
4. [Gestion du Mobilier](#4-gestion-du-mobilier)
5. [Catalogue](#5-catalogue)
6. [Types de Mobilier](#6-types-de-mobilier)
7. [Scanner](#7-scanner)
8. [Inventaire](#8-inventaire)
9. [Rapports d'Inventaire](#9-rapports-dinventaire)
10. [Localisations](#10-localisations)
11. [Mouvements](#11-mouvements)
12. [Import/Export CSV](#12-importexport-csv)
13. [Rapports & Exports Comptables](#13-rapports--exports-comptables)
14. [Mode Hors-Ligne (PWA)](#14-mode-hors-ligne-pwa)
15. [Administration](#15-administration)

---

## 1. Présentation

### 1.1 Objectif

GestMob est une application web progressive (PWA) permettant de gérer l'inventaire du mobilier de bureau de votre organisation.

### 1.2 Fonctionnalités principales

| Module | Description |
|--------|-------------|
| **Mobilier** | Gestion complète du parc mobilier |
| **Scanner** | Lecture de codes-barres (caméra ou lecteur) |
| **Inventaire** | Campagnes d'inventaire avec suivi temps réel |
| **Localisations** | Hiérarchie Sites > Bâtiments > Étages > Bureaux |
| **Mouvements** | Traçabilité des entrées, sorties, transferts |
| **Catalogue** | Références de mobilier standardisées |
| **Rapports** | Statistiques, exports CSV/Excel/PDF |
| **Mode hors-ligne** | Fonctionne sans connexion Internet |

---

## 2. Accès et Authentification

### 2.1 Connexion

1. Accédez à l'application via votre navigateur
2. Entrez votre **email** et **mot de passe**
3. Cliquez sur **"Se connecter"**

### 2.2 Première connexion

Si vous êtes nouveau :
1. Cliquez sur **"Créer un compte"**
2. Remplissez le formulaire (nom, email, mot de passe)
3. Votre compte sera créé avec le rôle **Lecteur**
4. Un administrateur pourra modifier vos droits

### 2.3 Rôles utilisateur

| Rôle | Droits |
|------|--------|
| **Admin** | Accès complet + gestion utilisateurs |
| **Gestionnaire** | Création/modification/suppression |
| **Lecteur** | Consultation uniquement |

---

## 3. Tableau de Bord

### 3.1 Accès

Menu → **Tableau de bord** (page d'accueil)

### 3.2 Indicateurs affichés

| KPI | Description |
|-----|-------------|
| Total mobilier | Nombre de meubles enregistrés |
| Mobilier affecté | Assigné à une localisation |
| En stock | Non assigné |
| En maintenance | En cours de réparation |

### 3.3 Graphiques

- **Répartition par état** : Neuf, Bon, Usagé, Hors service
- **Répartition par type** : Bureau, Chaise, Armoire, etc.

### 3.4 Mode hors-ligne

Cliquez sur **"Mode hors-ligne"** pour pré-charger les données et travailler sans connexion.

---

## 4. Gestion du Mobilier

### 4.1 Accès

Menu → **Mobilier**

### 4.2 Liste du mobilier

La page affiche tous les meubles avec :
- Code-barres
- Type et marque/modèle
- Localisation
- État
- Valeur d'achat

### 4.3 Filtres et recherche

| Filtre | Description |
|--------|-------------|
| Recherche | Par code-barres, marque ou modèle |
| Site | Filtrer par site |
| Type | Filtrer par type de mobilier |
| État | Filtrer par état |

### 4.4 Ajouter un mobilier

1. Cliquez sur **"Ajouter du mobilier"**
2. Remplissez le formulaire :
   - **Code-barres** (obligatoire, unique)
   - **Type de mobilier** (obligatoire)
   - Marque, modèle (optionnel)
   - Localisation (Site > Bâtiment > Étage > Bureau)
   - État, coût d'achat, etc.
3. Cliquez sur **"Enregistrer"**

### 4.5 Modifier un mobilier

1. Cliquez sur un mobilier dans la liste
2. Modifiez les champs souhaités
3. Cliquez sur **"Mettre à jour"**

### 4.6 Supprimer un mobilier

1. Ouvrez la fiche du mobilier
2. Cliquez sur **"Supprimer"**
3. Confirmez la suppression

---

## 5. Catalogue

### 5.1 Concept

Le catalogue contient des **références standardisées** de mobilier. Il permet de créer rapidement des meubles avec des informations pré-remplies.

### 5.2 Accès

Menu → **Catalogue**

### 5.3 Ajouter une référence

1. Cliquez sur **"Nouvelle référence"**
2. Remplissez : Type, Marque, Modèle, Fournisseur, Prix
3. Enregistrez

### 5.4 Utiliser le catalogue

Lors de la création d'un mobilier :
1. Cliquez sur **"Choisir du catalogue"**
2. Sélectionnez un article
3. Les champs sont pré-remplis automatiquement

---

## 6. Types de Mobilier

### 6.1 Accès

Menu → **Types mobilier**

### 6.2 Types par défaut

| Code | Label |
|------|-------|
| bureau | Bureau |
| chaise | Chaise |
| fauteuil | Fauteuil |
| armoire | Armoire |
| caisson | Caisson |
| etagere | Étagère |
| table_reunion | Table de réunion |
| cloison | Cloison |
| autre | Autre |

### 6.3 Ajouter un type personnalisé

1. Cliquez sur **"Ajouter un type"**
2. Renseignez le **code** et le **label**
3. Enregistrez

---

## 7. Scanner

### 7.1 Accès

Menu → **Scanner**

### 7.2 Modes de scan

| Mode | Description |
|------|-------------|
| **Saisie / Lecteur** | Champ texte + lecteur physique USB |
| **Caméra** | Scan via webcam ou caméra mobile |

### 7.3 Scanner avec lecteur physique

1. Sélectionnez **"Saisie / Lecteur"**
2. Placez le curseur dans le champ
3. Scannez avec votre lecteur
4. Le code est automatiquement recherché

### 7.4 Scanner avec caméra

1. Sélectionnez **"Caméra"**
2. Autorisez l'accès à la caméra
3. Pointez vers un code-barres
4. Attendez la détection automatique

### 7.5 Résultats du scan

- **Trouvé** : Affiche la fiche du mobilier
- **Non trouvé** : Propose de créer le mobilier

---

## 8. Inventaire

### 8.1 Concept

L'inventaire permet de vérifier la présence physique du mobilier sur un site.

### 8.2 Créer un inventaire

1. Menu → **Inventaire**
2. Cliquez sur **"Nouvel inventaire"**
3. Sélectionnez le **site**
4. Donnez un **nom**
5. Cliquez sur **"Créer"**

### 8.3 Scanner l'inventaire

1. Cliquez sur **"Scanner"** sur l'inventaire
2. Choisissez le mode (Saisie/Caméra)
3. Scannez les mobiliers

**Options de scan :**

| Option | Description |
|--------|-------------|
| Catalogue | Crée depuis le catalogue si inconnu |
| Créer auto | Création automatique si inconnu |
| Localisation | Où se trouve le mobilier scanné |
| Transfert auto | Crée un mouvement si localisation différente |

### 8.4 Historique en temps réel

- ✅ Vert : Mobilier trouvé
- 🟣 Violet : Nouveau mobilier créé
- 🔵 Bleu : Transfert créé
- 🟠 Orange : Non attendu sur ce site

### 8.5 Terminer l'inventaire

1. Cliquez sur **"Terminer l'inventaire"**
2. Le système calcule les résultats
3. Accédez au rapport détaillé

---

## 9. Rapports d'Inventaire

### 9.1 Accès

Depuis la page **Inventaire** → Cliquez sur **"Rapport"**

### 9.2 Contenu du rapport

| Section | Description |
|---------|-------------|
| **Résumé** | Statistiques globales |
| **Manquants** | Liste des mobiliers non trouvés |
| **Trouvés** | Liste des mobiliers scannés |

### 9.3 Indicateurs

| Indicateur | Description |
|------------|-------------|
| Attendu | Nombre de mobiliers assignés au site |
| Trouvés | Mobiliers scannés et attendus |
| Manquants | Mobiliers attendus mais non scannés |
| Non référencés | Scannés mais non attendus |
| Valeur manquante | Coût total des manquants |

### 9.4 Filtres

- Par bâtiment, étage, bureau
- Recherche textuelle

### 9.5 Export PDF

Cliquez sur **"Exporter PDF"** pour télécharger le rapport.

---

## 10. Localisations

### 10.1 Hiérarchie

\`\`\`
Site
└── Bâtiment
    └── Étage
        └── Bureau
\`\`\`

### 10.2 Créer une localisation

1. Menu → **Localisations**
2. Cliquez sur **"Nouvelle localisation"**
3. Sélectionnez le **type** et le **parent**
4. Remplissez le nom

### 10.3 Règles

| Type | Parent requis |
|------|---------------|
| Site | Aucun |
| Bâtiment | Site |
| Étage | Bâtiment |
| Bureau | Étage |

---

## 11. Mouvements

### 11.1 Types

| Type | Description |
|------|-------------|
| Entrée | Réception de nouveau mobilier |
| Sortie | Sortie définitive |
| Transfert | Déplacement entre localisations |
| Maintenance | Envoi en réparation |
| Mise au rebut | Destruction/recyclage |

### 11.2 Créer un mouvement

1. Menu → **Mouvements**
2. Cliquez sur **"Nouveau mouvement"**
3. Sélectionnez le mobilier et le type
4. Remplissez les détails

---

## 12. Import/Export CSV

### 12.1 Accès

Menu → **Import CSV**

### 12.2 Format CSV Mobilier

\`\`\`csv
code_barres;type_mobilier;marque;modele;localisation;cout_achat;etat
MOB-001;bureau;Steelcase;Series 7;Site > Bât A > Étage 1 > Bureau 101;850;neuf
\`\`\`

### 12.3 Processus d'import

1. Téléchargez le **modèle CSV**
2. Remplissez vos données
3. **Prévisualisez** pour vérifier
4. **Importez**

---

## 13. Rapports & Exports Comptables

### 13.1 Accès

Menu → **Rapports**

### 13.2 Onglet Rapports

- Statistiques du mobilier
- Export JSON/CSV

### 13.3 Onglet Exports Comptables

Exports pour logiciels comptables (Sage, Cegid, EBP, QuickBooks, Odoo).

#### Filtres globaux

| Filtre | Description |
|--------|-------------|
| Site | Filtrer par site |
| Date début/fin | Période |
| Format | CSV ou Excel (.xlsx) |

#### Export Immobilisations

Registre des actifs avec amortissements :
- Code-barres, désignation
- Date et coût d'acquisition
- Durée et calcul d'amortissement
- Valeur nette comptable

#### Export Mouvements

Historique des entrées, sorties, transferts.

#### Export Inventaires

Résultats avec valorisation des écarts.

---

## 14. Mode Hors-Ligne (PWA)

### 14.1 Installation

**Sur mobile :**
1. Ouvrez l'app dans le navigateur
2. Menu → "Ajouter à l'écran d'accueil"

**Sur ordinateur :**
1. Cliquez sur l'icône d'installation dans la barre d'adresse

### 14.2 Pré-charger les données

1. Tableau de bord → **"Mode hors-ligne"**
2. Attendez la fin du chargement

### 14.3 Ce qui fonctionne hors-ligne

| Fonctionnalité | Disponibilité |
|----------------|---------------|
| ✅ Consultation mobilier | Données en cache |
| ✅ Scanner codes-barres | Complet |
| ✅ Scans d'inventaire | Stockés localement |
| ✅ Création mobilier | Mis en file d'attente |

### 14.4 Synchronisation automatique

Quand la connexion revient :
1. L'app détecte la connexion
2. Les actions en attente sont envoyées
3. Notification de confirmation

---

## 15. Administration

### 15.1 Gestion des utilisateurs

Menu → **Utilisateurs** (Admin uniquement)

- Voir la liste des utilisateurs
- Modifier les rôles
- Activer/Désactiver des comptes

### 15.2 Rôles et permissions

| Action | Admin | Gestionnaire | Lecteur |
|--------|-------|--------------|---------|
| Consulter | ✅ | ✅ | ✅ |
| Créer/Modifier | ✅ | ✅ | ❌ |
| Supprimer | ✅ | ✅ | ❌ |
| Gérer utilisateurs | ✅ | ❌ | ❌ |

---

## Annexes

### Navigateurs supportés

| Navigateur | Version | PWA |
|------------|---------|-----|
| Chrome | 80+ | ✅ |
| Firefox | 75+ | ⚠️ |
| Safari | 14+ | ✅ |
| Edge | 80+ | ✅ |

### Formats d'export

| Format | Extension | Usage |
|--------|-----------|-------|
| CSV | .csv | Universel |
| Excel | .xlsx | Comptabilité |
| JSON | .json | Intégration |
| PDF | .pdf | Archivage |

### Logiciels comptables compatibles

- Sage (50, 100, Business Cloud)
- Cegid
- EBP
- QuickBooks
- Odoo
- Excel / LibreOffice

---

*Version 3.1.0*
`;

const DocumentationPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('');
  const [showSidebar, setShowSidebar] = useState(false);

  // Extract sections from markdown
  const sections = DOCUMENTATION_CONTENT
    .split('\n')
    .filter(line => line.startsWith('## '))
    .map(line => ({
      id: line.replace('## ', '').toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      title: line.replace('## ', '')
    }));

  // Filter content based on search
  const filteredContent = searchTerm
    ? DOCUMENTATION_CONTENT
        .split('\n')
        .filter(line => line.toLowerCase().includes(searchTerm.toLowerCase()))
        .join('\n')
    : DOCUMENTATION_CONTENT;

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
    setShowSidebar(false);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="documentation-page">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon" 
              className="lg:hidden"
              onClick={() => setShowSidebar(!showSidebar)}
            >
              {showSidebar ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <Book className="h-6 w-6 text-blue-600" />
            <h1 className="text-xl font-bold text-slate-900">Documentation</h1>
          </div>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="doc-search-input"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto flex">
        {/* Sidebar - Table of contents */}
        <aside className={`
          fixed lg:sticky top-16 left-0 z-20 h-[calc(100vh-4rem)] w-72 bg-white border-r
          transform transition-transform duration-200 lg:transform-none
          ${showSidebar ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <ScrollArea className="h-full p-4">
            <h2 className="font-semibold text-slate-900 mb-4">Table des matières</h2>
            <nav className="space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`
                    w-full text-left px-3 py-2 rounded-md text-sm transition-colors
                    flex items-center gap-2 hover:bg-slate-100
                    ${activeSection === section.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-600'}
                  `}
                >
                  <ChevronRight className={`h-4 w-4 transition-transform ${activeSection === section.id ? 'rotate-90' : ''}`} />
                  <span className="truncate">{section.title}</span>
                </button>
              ))}
            </nav>
          </ScrollArea>
        </aside>

        {/* Overlay for mobile */}
        {showSidebar && (
          <div 
            className="fixed inset-0 bg-black/20 z-10 lg:hidden"
            onClick={() => setShowSidebar(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0 p-6 lg:p-8">
          <Card className="bg-white shadow-sm">
            <CardContent className="p-6 lg:p-8">
              <div className="documentation-content">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({node, children, ...props}) => (
                      <h1 className="text-3xl font-bold text-slate-900 mb-6 pb-4 border-b" {...props}>{children}</h1>
                    ),
                    h2: ({node, children, ...props}) => {
                      const id = String(children).toLowerCase().replace(/[^a-z0-9]+/g, '-');
                      return <h2 id={id} className="text-2xl font-semibold text-slate-800 mt-10 mb-4 pb-2 border-b scroll-mt-20" {...props}>{children}</h2>;
                    },
                    h3: ({node, children, ...props}) => (
                      <h3 className="text-lg font-medium text-slate-700 mt-6 mb-3" {...props}>{children}</h3>
                    ),
                    h4: ({node, children, ...props}) => (
                      <h4 className="text-base font-medium text-slate-600 mt-4 mb-2" {...props}>{children}</h4>
                    ),
                    p: ({node, children, ...props}) => (
                      <p className="text-slate-600 mb-4 leading-relaxed" {...props}>{children}</p>
                    ),
                    ul: ({node, children, ...props}) => (
                      <ul className="list-disc list-inside mb-4 space-y-1 text-slate-600" {...props}>{children}</ul>
                    ),
                    ol: ({node, children, ...props}) => (
                      <ol className="list-decimal list-inside mb-4 space-y-1 text-slate-600" {...props}>{children}</ol>
                    ),
                    li: ({node, children, ...props}) => (
                      <li className="text-slate-600" {...props}>{children}</li>
                    ),
                    table: ({node, children, ...props}) => (
                      <div className="overflow-x-auto mb-6">
                        <table className="min-w-full border border-slate-200 rounded-lg" {...props}>{children}</table>
                      </div>
                    ),
                    thead: ({node, children, ...props}) => (
                      <thead className="bg-slate-100" {...props}>{children}</thead>
                    ),
                    th: ({node, children, ...props}) => (
                      <th className="px-4 py-2 text-left text-sm font-semibold text-slate-700 border-b border-slate-200" {...props}>{children}</th>
                    ),
                    td: ({node, children, ...props}) => (
                      <td className="px-4 py-2 text-sm text-slate-600 border-b border-slate-100" {...props}>{children}</td>
                    ),
                    code: ({node, inline, children, ...props}) => (
                      inline 
                        ? <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm text-slate-800 font-mono" {...props}>{children}</code>
                        : <code className="block bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm font-mono" {...props}>{children}</code>
                    ),
                    pre: ({node, children, ...props}) => (
                      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto mb-4 text-sm" {...props}>{children}</pre>
                    ),
                    blockquote: ({node, children, ...props}) => (
                      <blockquote className="border-l-4 border-blue-500 bg-blue-50 pl-4 py-2 mb-4 text-slate-700 italic" {...props}>{children}</blockquote>
                    ),
                    a: ({node, children, href, ...props}) => (
                      <a href={href} className="text-blue-600 hover:underline" {...props}>{children}</a>
                    ),
                    hr: ({node, ...props}) => (
                      <hr className="my-8 border-slate-200" {...props} />
                    ),
                    strong: ({node, children, ...props}) => (
                      <strong className="font-semibold text-slate-900" {...props}>{children}</strong>
                    ),
                  }}
                >
                  {filteredContent}
                </ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
};

export default DocumentationPage;
